import { APIError } from '@cartwhl/common/api/apiRequest';
import postMascot, {
  type MascotJobPayload,
} from '@cartwhl/common/api/postMascot';
import postMediaUpload, {
  type MediaUploadItem,
  type MediaUploadResponseItem,
} from '@cartwhl/common/api/postMediaUpload';
import { navigateTo } from '@cartwhl/common/navigation';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import { waitForAsyncState } from '@cartwhl/common/utils';
import { uploadBlobToPresignedUrl } from '@cartwhl/common/utils/mediaUploadThumbnail';
import { handleOrgCreditBlockApiError } from '@cartwhl/common/utils/handleOrgCreditBlockApiError';
import type { FilesListFile } from '@cartwhl/ui/input/FilesList';

import * as globalStores from '../../stores/global';
import {
  awaitMediaUploadThumbnail,
  clearMediaUploadThumbnailCache,
} from '../shared/mediaUpload/mediaUploadThumbnailCache';
import type { AssetLibraryMediaSelection } from '../AssetLibrary/assetLibraryMediaSelection';
import type { MascotRow, UploadModalState } from './types';

type RowFilesListRefs = {
  getVideoFiles: () => FilesListFile[];
  getCharRefFiles: () => FilesListFile[];
  getEnvFiles: () => FilesListFile[];
};

function generateRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

type Deps = {
  global: typeof globalStores;
};

type State = {
  batchName: string;
  mascotRows: MascotRow[];
  uploadModalState: UploadModalState;
  uploadedFilesCount: number;
  totalFilesCount: number;
};

const createInitialRow = (): MascotRow => ({
  id: generateRowId(),
  characterCount: 'single',
  videoFileCount: 0,
  characterRefFilesCount: 0,
  environmentFileCount: 0,
  prompt: '',
  libraryMotionRef: null,
  libraryCharRefs: [],
  libraryBackgroundRef: null,
});

const initialState: State = {
  batchName: '',
  mascotRows: [createInitialRow()],
  uploadModalState: 'idle',
  uploadedFilesCount: 0,
  totalFilesCount: 0,
};

class BatchMascotUploadStore extends BaseStore<State, Deps> {
  private uploadAbortController: AbortController | null = null;
  private rowFilesListRefs = new Map<string, RowFilesListRefs>();

  constructor(deps: Deps) {
    super(initialState, deps);
  }

  registerRowFilesListRefs(rowId: string, refs: RowFilesListRefs) {
    this.rowFilesListRefs.set(rowId, refs);
  }

  unregisterRowFilesListRefs(rowId: string) {
    this.rowFilesListRefs.delete(rowId);
  }

  reset() {
    if (this.uploadAbortController) {
      this.uploadAbortController.abort();
      this.uploadAbortController = null;
    }
    Object.assign(this.state, {
      ...initialState,
      mascotRows: [createInitialRow()],
    });
    clearMediaUploadThumbnailCache();
  }

  destroy() {
    if (this.uploadAbortController) {
      this.uploadAbortController.abort();
    }
    super.destroy();
  }

  updateBatchName(name: string) {
    this.state.batchName = name;
  }

  addMascotRow() {
    this.state.mascotRows = [...this.state.mascotRows, createInitialRow()];
  }

  removeMascotRow(id: string) {
    if (this.state.mascotRows.length <= 1) return;
    this.unregisterRowFilesListRefs(id);
    this.state.mascotRows = this.state.mascotRows.filter((r) => r.id !== id);
  }

  updateMascotRow<K extends keyof Omit<MascotRow, 'id'>>(
    id: string,
    field: K,
    value: MascotRow[K],
  ) {
    const row = this.state.mascotRows.find((r) => r.id === id);
    if (row) {
      row[field] = value;
    }
  }

  setRowLibraryMotionRef(rowId: string, selection: AssetLibraryMediaSelection | null) {
    const row = this.state.mascotRows.find((r) => r.id === rowId);
    if (row) {
      row.libraryMotionRef = selection;
      const uploadCount =
        this.rowFilesListRefs.get(rowId)?.getVideoFiles().length ?? 0;
      row.videoFileCount = uploadCount + (selection ? 1 : 0);
    }
  }

  setRowLibraryCharRefs(
    rowId: string,
    selections: AssetLibraryMediaSelection[],
  ) {
    const row = this.state.mascotRows.find((r) => r.id === rowId);
    if (!row) return;

    row.libraryCharRefs = selections;
    row.characterRefFilesCount =
      (this.rowFilesListRefs.get(rowId)?.getCharRefFiles().length ?? 0) +
      selections.length;
  }

  setRowLibraryBackgroundRef(
    rowId: string,
    selection: AssetLibraryMediaSelection | null,
  ) {
    const row = this.state.mascotRows.find((r) => r.id === rowId);
    if (row) {
      row.libraryBackgroundRef = selection;
      const uploadCount =
        this.rowFilesListRefs.get(rowId)?.getEnvFiles().length ?? 0;
      row.environmentFileCount = uploadCount + (selection ? 1 : 0);
    }
  }

  removeRowLibraryCharRef(rowId: string, mediaID: string) {
    const row = this.state.mascotRows.find((r) => r.id === rowId);
    if (!row) return;

    row.libraryCharRefs = row.libraryCharRefs.filter(
      (item) => item.mediaID !== mediaID,
    );
    row.characterRefFilesCount =
      (this.rowFilesListRefs.get(rowId)?.getCharRefFiles().length ?? 0) +
      row.libraryCharRefs.length;
  }

  async runProcess() {
    if (this.deps.global.authStore.state.authStatus !== 'user') {
      this.deps.global.requestAppletSignInModal();
      return;
    }

    const validRows = this.state.mascotRows.filter((r) => {
      const refs = this.rowFilesListRefs.get(r.id);
      const hasVideo =
        (refs?.getVideoFiles().length ?? 0) >= 1 || Boolean(r.libraryMotionRef);
      const hasCharRefs =
        (refs?.getCharRefFiles().length ?? 0) >= 1 ||
        r.libraryCharRefs.length >= 1;

      return hasVideo && hasCharRefs && r.prompt.trim().length > 0;
    });

    if (validRows.length === 0) {
      globalStores.noticesStore.error(
        'Each job needs a video, character reference(s), and prompt.',
      );
      return;
    }

    let apiKey: string;
    try {
      const apiKeyData = await waitForAsyncState(
        this.deps.global.userStore.state,
        (s) => s.apiKey,
      );
      if (!apiKeyData) {
        globalStores.noticesStore.error(
          'Failed to authenticate. Please try again.',
        );
        return;
      }
      apiKey = apiKeyData.apiKey;
    } catch (err) {
      console.error('Failed to get API key:', err);
      globalStores.noticesStore.error(
        'Failed to authenticate. Please try again.',
      );
      return;
    }

    this.uploadAbortController = new AbortController();
    this.state.uploadedFilesCount = 0;

    const userEmail = this.deps.global.userStore.state.userInfo?.email ?? '';
    const emailSubscribers = userEmail ? [userEmail] : [];

    let totalFiles = 0;
    for (const row of validRows) {
      const refs = this.rowFilesListRefs.get(row.id);
      const charUploadCount = refs?.getCharRefFiles().length ?? 0;
      const envUploadCount = refs?.getEnvFiles().length ?? 0;
      const hasVideoUpload = (refs?.getVideoFiles().length ?? 0) >= 1;
      totalFiles +=
        (hasVideoUpload ? 0 : row.libraryMotionRef ? 0 : 1) +
        charUploadCount +
        row.libraryCharRefs.length +
        (row.libraryBackgroundRef ? 0 : envUploadCount);
    }
    this.state.totalFilesCount = totalFiles;

    try {
      let completedCount = 0;
      const abortSignal = this.uploadAbortController.signal;
      const jobPayloads: MascotJobPayload[] = [];

      for (const row of validRows) {
        if (abortSignal.aborted) return;

        const refs = this.rowFilesListRefs.get(row.id);
        if (!refs) {
          globalStores.noticesStore.error(
            'Could not access files. Please try again.',
          );
          throw new Error('Row refs not registered');
        }

        const videoFiles = refs.getVideoFiles();
        const charFiles = refs.getCharRefFiles();
        const envFiles = refs.getEnvFiles();

        const videoFile = videoFiles[0];
        const motionFromLibrary = row.libraryMotionRef;
        if (!videoFile && !motionFromLibrary) continue;

        const charFileItems = charFiles;
        const charFileObjs = charFileItems.map((f) => f.file);
        const libraryCharMediaIDs = row.libraryCharRefs.map((item) => item.mediaID);
        if (charFileObjs.length === 0 && libraryCharMediaIDs.length === 0) {
          continue;
        }

        const envFile = envFiles[0];
        const backgroundFromLibrary = row.libraryBackgroundRef;

        this.state.uploadModalState = 'getting_links';

        const mediaItems: MediaUploadItem[] = [];
        let videoFileObj: File | null = null;

        if (videoFile) {
          videoFileObj = videoFile.file;
          const videoMeta = await this.getVideoMetadata(videoFileObj);

          if (row.characterCount === 'single' && videoMeta.duration < 3) {
            const jobLabel = videoFileObj.name;
            throw new Error(
              `"${jobLabel}" is ${videoMeta.duration.toFixed(1)}s long. Single-character mode requires videos of at least 3 seconds.`,
            );
          }

          mediaItems.push({
            extension: this.getFileExtension(videoFileObj.name),
            name: this.getFileNameWithoutExtension(videoFileObj.name),
            duration: videoMeta.duration,
            resolution: videoMeta.resolution,
          });
        }

        for (let i = 0; i < charFileObjs.length; i++) {
          const f = charFileObjs[i];
          mediaItems.push({
            extension: this.getFileExtension(f.name),
            name: `char-${i}-${this.getFileNameWithoutExtension(f.name)}`,
          });
        }

        const envFileObj = envFile?.file;
        if (envFileObj && !backgroundFromLibrary) {
          mediaItems.push({
            extension: this.getFileExtension(envFileObj.name),
            name: `env-${this.getFileNameWithoutExtension(envFileObj.name)}`,
          });
        }

        let uploadedMediaIDs: string[] = [];

        if (mediaItems.length > 0) {
          const { data: uploadData, error: uploadError } = await postMediaUpload(
            this.deps.global.userStore.state.userInfo?.userID,
            mediaItems,
            apiKey,
          );

          if (uploadError || !uploadData) {
            throw new Error(uploadError?.message || 'Failed to get upload links');
          }

          if (abortSignal.aborted) return;

          this.state.uploadModalState = 'uploading';

          const uploads: MediaUploadResponseItem[] = uploadData.mediaUploads;
          let idx = 0;

          if (videoFileObj) {
            await this.uploadMediaWithThumbnail(
              uploads[idx].mediaUploadURL,
              uploads[idx].thumbnailUploadURL,
              videoFile,
              videoFileObj.type || 'video/mp4',
              abortSignal,
            );
            completedCount++;
            this.state.uploadedFilesCount = completedCount;
            idx++;
          }

          for (let i = 0; i < charFileItems.length; i++) {
            if (abortSignal.aborted) return;
            const listFile = charFileItems[i];
            await this.uploadMediaWithThumbnail(
              uploads[idx].mediaUploadURL,
              uploads[idx].thumbnailUploadURL,
              listFile,
              listFile.file.type || 'image/png',
              abortSignal,
            );
            completedCount++;
            this.state.uploadedFilesCount = completedCount;
            idx++;
          }

          if (envFileObj && !backgroundFromLibrary && envFile) {
            if (abortSignal.aborted) return;
            await this.uploadMediaWithThumbnail(
              uploads[idx].mediaUploadURL,
              uploads[idx].thumbnailUploadURL,
              envFile,
              envFileObj.type || 'image/png',
              abortSignal,
            );
            completedCount++;
            this.state.uploadedFilesCount = completedCount;
          }

          uploadedMediaIDs = uploads.map((upload) => upload.mediaID);
        }

        const videoMediaID =
          motionFromLibrary?.mediaID ??
          uploadedMediaIDs[0];
        const charMediaIDs = [
          ...libraryCharMediaIDs,
          ...uploadedMediaIDs.slice(
            videoFileObj ? 1 : 0,
            (videoFileObj ? 1 : 0) + charFileObjs.length,
          ),
        ];
        const envMediaID =
          backgroundFromLibrary?.mediaID ??
          (envFileObj && !backgroundFromLibrary
            ? uploadedMediaIDs[uploadedMediaIDs.length - 1]
            : undefined);

        jobPayloads.push({
          inputVideoMediaID: videoMediaID,
          characterReferenceMediaIDs: charMediaIDs,
          prompt: row.prompt.trim(),
          mascotName:
            motionFromLibrary?.label ??
            (videoFileObj
              ? this.getFileNameWithoutExtension(videoFileObj.name)
              : 'mascot'),
          environmentImageMediaID: envMediaID,
          engine: row.characterCount === 'single' ? 'kling' : 'scail',
        });
      }

      if (abortSignal.aborted) return;

      this.state.uploadModalState = 'starting_process';

      const { data: mascotData, error: mascotError } = await postMascot(
        {
          jobs: jobPayloads,
          batchName: this.state.batchName || undefined,
          createdByUser: this.deps.global.userStore.state.userInfo?.userID,
          emailSubscribers,
        },
        apiKey,
      );

      if (mascotError || !mascotData) {
        if (
          mascotError instanceof APIError &&
          handleOrgCreditBlockApiError(
            mascotError,
            this.deps.global.userStore.state.organizationInfo,
          )
        ) {
          this.state.uploadModalState = 'idle';
          return;
        }
        throw new Error(
          mascotError?.message || 'Failed to create mascot batch',
        );
      }

      this.state.uploadModalState = 'idle';
      navigateTo(`/mascot/${mascotData.mascotID}`);
    } catch (error) {
      if (
        (error as Error).message === 'Upload cancelled' ||
        (error as Error).name === 'AbortError'
      ) {
        return;
      }
      console.error('Error running process:', error);
      globalStores.noticesStore.error(
        (error as Error).message || 'Failed to run process',
      );
      this.state.uploadModalState = 'idle';
    }
  }

  cancelUpload() {
    if (this.uploadAbortController) {
      this.uploadAbortController.abort();
      this.uploadAbortController = null;
    }
    this.state.uploadModalState = 'idle';
    this.state.uploadedFilesCount = 0;
    this.state.totalFilesCount = 0;
  }

  private getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
  }

  private getFileNameWithoutExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    return lastDot > 0 ? filename.substring(0, lastDot) : filename;
  }

  private async getVideoMetadata(
    file: File,
  ): Promise<{ duration: number; resolution: string }> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve({
          duration: video.duration,
          resolution: `${video.videoWidth}x${video.videoHeight}`,
        });
      };
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };
      video.src = URL.createObjectURL(file);
    });
  }

  private async uploadMediaWithThumbnail(
    mediaUploadURL: string,
    thumbnailUploadURL: string,
    listFile: FilesListFile,
    contentType: string,
    signal: AbortSignal,
  ): Promise<void> {
    const thumbnail = await awaitMediaUploadThumbnail(listFile.id, signal);

    await Promise.all([
      uploadBlobToPresignedUrl(
        mediaUploadURL,
        listFile.file,
        contentType,
        signal,
      ),
      thumbnail
        ? uploadBlobToPresignedUrl(
            thumbnailUploadURL,
            thumbnail.blob,
            thumbnail.contentType,
            signal,
          )
        : Promise.resolve(),
    ]);
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: BatchMascotUploadStore,
  displayName: 'BatchMascotUploadStore',
  staticDeps: { global: globalStores },
});

export type { BatchMascotUploadStore };
